/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { useCreateGlossary } from '@utils/api/glossaries/use-create-glossary.tsx';
import { useGetSupportedLanguagePairs } from '@utils/api/glossaries/use-get-supported-languages.tsx';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import toast from 'react-hot-toast';
import { CreateGlossaryRequest } from '@utils/types/glossary';
import Papa from 'papaparse';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type Props = {
  control: Control<CreateGlossaryRequest>;
  errors: FieldErrors<CreateGlossaryRequest>;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  reset: UseFormReset<CreateGlossaryRequest>;
  handleSubmit: UseFormHandleSubmit<CreateGlossaryRequest>;
  register: UseFormRegister<CreateGlossaryRequest>;
  setValue: UseFormSetValue<CreateGlossaryRequest>;
};

export default function CreateGlossaryModal({
  control,
  errors,
  isModalOpen,
  setIsModalOpen,
  reset,
  handleSubmit,
  register,
  setValue
}: Props) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';
  const successColor = isDark ? 'rgb(74,222,128)' : 'rgb(22,163,74)';
  const { mutate: createGlossary, isLoading } = useCreateGlossary();
  const { data: supportedLanguages, isLoading: isLoadingLanguages } =
    useGetSupportedLanguagePairs();
  const [selectedSourceLanguage, setSelectedSourceLanguage] =
    useState<string>('');
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract unique source languages with names
  const sourceLanguageOptions = useMemo(() => {
    if (!supportedLanguages?.language_pairs) return [];

    const uniqueSources = new Map<
      string,
      { name: string; services: Set<string> }
    >();

    supportedLanguages.language_pairs.forEach(pair => {
      if (!uniqueSources.has(pair.source_language)) {
        uniqueSources.set(pair.source_language, {
          name: pair.source_language_name,
          services: new Set()
        });
      }
      uniqueSources.get(pair.source_language)?.services.add(pair.service);
    });

    return Array.from(uniqueSources.entries())
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .map(([code, data]) => ({
        label: `${data.name} (${code})`,
        value: code
      }));
  }, [supportedLanguages]);

  // Get target language options based on selected source language
  const targetLanguageOptions = useMemo(() => {
    if (!supportedLanguages?.language_pairs || !selectedSourceLanguage)
      return [];

    // Get all services that support the selected source language
    const availableServices = new Set<string>();
    supportedLanguages.language_pairs
      .filter(pair => pair.source_language === selectedSourceLanguage)
      .forEach(pair => availableServices.add(pair.service));

    // Pick ONE service to use (prioritize DeepL if available)
    const selectedService = availableServices.has('deepl')
      ? 'deepl'
      : availableServices.has('google')
        ? 'google'
        : null;

    if (!selectedService) return [];

    // Get unique target languages from pairs with the selected source language
    // and ONLY the selected service (e.g., if EN is in both DeepL and Google,
    // we only show DeepL target languages)
    const uniqueTargets = new Map<string, string>();
    supportedLanguages.language_pairs
      .filter(
        pair =>
          pair.source_language === selectedSourceLanguage &&
          pair.service === selectedService
      )
      .forEach(pair => {
        uniqueTargets.set(pair.target_language, pair.target_language_name);
      });

    return Array.from(uniqueTargets.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([code, name]) => ({
        label: `${name} (${code})`,
        value: code
      }));
  }, [supportedLanguages, selectedSourceLanguage]);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedSourceLanguage('');
      setUploadedFileName('');
    }
  }, [isModalOpen]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error(t('please-upload-a-valid-csv-file'));
      return;
    }

    // Use PapaParse to parse the CSV file
    Papa.parse(file, {
      complete: results => {
        try {
          // Check if parsing had errors
          if (results.errors && results.errors.length > 0) {
            toast.error(t('error-reading-file'));
            return;
          }

          // Validate and process the parsed data
          const rows = results.data as string[][];

          // Filter out empty rows
          const validRows = rows.filter(
            row => row && row.length >= 2 && row[0]?.trim() && row[1]?.trim()
          );

          if (validRows.length === 0) {
            toast.error(t('csv-must-contain-at-least-one-term-pair'));
            return;
          }

          // Validate that all rows have exactly 2 columns (source, target)
          const invalidRows = validRows.filter(row => row.length !== 2);
          if (invalidRows.length > 0) {
            toast.error(t('all-rows-must-have-exactly-two-columns'));
            return;
          }

          // Convert back to clean CSV format (removes extra whitespace, handles special chars)
          const csvContent = validRows
            .map(row => `${row[0].trim()},${row[1].trim()}`)
            .join('\n');

          // Don't trigger validation on setValue to avoid API calls
          setValue('entries_csv', csvContent, {
            shouldValidate: false,
            shouldDirty: true
          });
          setUploadedFileName(file.name);
          toast.success(t('csv-file-uploaded-successfully'));
        } catch (error) {
          toast.error(t('error-reading-file'));
          // Reset the file input on error
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      error: () => {
        toast.error(t('error-reading-file'));
        // Reset the file input on error
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      skipEmptyLines: true
    });
  };

  const handleClearFile = () => {
    setUploadedFileName('');
    setValue('entries_csv', '', { shouldValidate: false, shouldDirty: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmitCreate = (data: CreateGlossaryRequest) => {
    createGlossary(data, {
      onSuccess: () => {
        toast.success(t('glossary-created-successfully'));
        setIsModalOpen(false);
        setSelectedSourceLanguage('');
        setUploadedFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        reset({
          name: '',
          description: '',
          source_language: '',
          target_language: '',
          entries_csv: ''
        });
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error?.response?.data.detail ||
          error?.response?.data.error ||
          error?.response?.data.message ||
          t('an-error-occurred-while-creating-the-glossary');

        toast.error(errorMessage);
      }
    });
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      closeOnOutsideClick={false}
      size="large"
      title={t('create-glossary')}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => {
            setIsModalOpen(false);
            setSelectedSourceLanguage('');
            setUploadedFileName('');
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            reset();
          }}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />,
        <Button
          key="create"
          type="submit"
          onClick={handleSubmit(onSubmitCreate)}
          label={t('create')}
          size="small"
          variant="primary"
          disabled={isLoading || isLoadingLanguages}
        />
      ]}
    >
      <form onSubmit={handleSubmit(onSubmitCreate)}>
        <div className="grid grid-cols-1 gap-4">
          <Input
            id="name"
            label={t('glossary-name')}
            type="text"
            placeholder={t('glossary-name-placeholder')}
            {...register('name')}
            error={errors.name && errors.name.message}
          />

          <Input
            id="description"
            label={t('description')}
            type="text"
            multiline
            placeholder={t('glossary-description-placeholder')}
            {...register('description')}
            error={errors.description && errors.description.message}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="source_language"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="source_language"
                  label={t('source-language')}
                  options={sourceLanguageOptions}
                  value={sourceLanguageOptions.find(
                    opt => opt.value === field.value
                  )}
                  onChange={selectedOption => {
                    const value = selectedOption?.value || '';
                    field.onChange(value);
                    setSelectedSourceLanguage(value);
                  }}
                  placeholder={
                    isLoadingLanguages
                      ? t('loading')
                      : t('select-source-language')
                  }
                  error={
                    errors.source_language && errors.source_language.message
                  }
                />
              )}
            />

            <Controller
              name="target_language"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="target_language"
                  label={t('target-language')}
                  options={targetLanguageOptions}
                  value={targetLanguageOptions.find(
                    opt => opt.value === field.value
                  )}
                  onChange={selectedOption => {
                    field.onChange(selectedOption?.value || '');
                  }}
                  placeholder={
                    isLoadingLanguages
                      ? t('loading')
                      : !selectedSourceLanguage
                        ? t('select-source-language-first')
                        : t('select-target-language')
                  }
                  error={
                    errors.target_language && errors.target_language.message
                  }
                />
              )}
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: getColour('normalText') }}
            >
              {t('term-pairs')}
            </label>

            {/* File Upload Section */}
            <div className="mb-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-file-upload"
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  label={uploadedFileName || t('upload-csv-file')}
                  size="small"
                  variant="secondary"
                  disabled={isLoading}
                />
                {uploadedFileName && (
                  <Button
                    type="button"
                    onClick={handleClearFile}
                    label={t('clear')}
                    size="small"
                    variant="smoke"
                    disabled={isLoading}
                  />
                )}
              </div>
              {uploadedFileName && (
                <p className="text-xs mt-1" style={{ color: successColor }}>
                  {t('file-loaded')}: {uploadedFileName}
                </p>
              )}
            </div>

            {/* Manual CSV Input */}
            <div className="relative">
              <p
                className="text-xs mb-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('or-enter-csv-manually')}
              </p>
              <Input
                id="entries_csv"
                type="text"
                multiline
                placeholder={t('glossary-entries-csv-placeholder')}
                {...register('entries_csv')}
                error={errors.entries_csv && errors.entries_csv.message}
              />
              <p
                className="text-xs mt-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('glossary-csv-format-help')}
              </p>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
